import { compressImageDataUrl, applyImageDisplayWidth } from "../components/ImageResizeBar";

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

export function captureEditorRange(editor) {
    if (!editor) return null;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return null;
    return range.cloneRange();
}

export function rememberEditorSelection(editor) {
    if (!editor) return;
    const range = captureEditorRange(editor);
    if (range) editor._lastComposeRange = range;
}

function placeCaretAfter(node) {
    const sel = window.getSelection();
    if (!sel || !node?.parentNode) return;
    const range = document.createRange();
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
}

export function wrapComposeImage(img) {
    if (!img || img.closest(".crm-resizable-img-wrap")) return img?.closest(".crm-resizable-img-wrap") || img;

    const wrap = document.createElement("span");
    wrap.className = "crm-resizable-img-wrap";
    wrap.contentEditable = "false";

    img.classList.add("crm-compose-img");
    if (!img.style.width && !img.getAttribute("width")) {
        applyImageDisplayWidth(img, Math.min(img.naturalWidth || 280, 280));
    }

    const parent = img.parentNode;
    if (parent) {
        parent.insertBefore(wrap, img);
        wrap.appendChild(img);
    } else {
        wrap.appendChild(img);
    }

    HANDLES.forEach((pos) => {
        const handle = document.createElement("span");
        handle.className = `crm-img-resize-handle crm-img-resize-handle--${pos}`;
        handle.dataset.handle = pos;
        wrap.appendChild(handle);
    });

    bindResizeHandles(wrap, img);
    return wrap;
}

function bindResizeHandles(wrap, img) {
    wrap.querySelectorAll(".crm-img-resize-handle").forEach((handle) => {
        handle.addEventListener("mousedown", (e) => {
            e.preventDefault();
            e.stopPropagation();

            const pos = handle.dataset.handle;
            const startX = e.clientX;
            const startY = e.clientY;
            const rect = img.getBoundingClientRect();
            const startW = rect.width;
            const startH = rect.height;
            const aspect = startW / Math.max(startH, 1);
            const lockAspect = e.shiftKey;

            const onMove = (ev) => {
                let dx = ev.clientX - startX;
                let dy = ev.clientY - startY;

                if (pos.includes("w")) dx = -dx;
                if (pos.includes("n")) dy = -dy;

                let newW = startW;
                let newH = startH;

                if (pos === "e" || pos === "w") {
                    newW = clamp(startW + dx, 60, 900);
                    newH = lockAspect ? newW / aspect : clamp(startH + dy, 40, 900);
                } else if (pos === "n" || pos === "s") {
                    newH = clamp(startH + dy, 40, 900);
                    newW = lockAspect ? newH * aspect : clamp(startW + dx, 60, 900);
                } else {
                    newW = clamp(startW + dx, 60, 900);
                    newH = lockAspect ? newW / aspect : clamp(startH + dy, 40, 900);
                }

                img.style.width = `${Math.round(newW)}px`;
                img.style.maxWidth = `${Math.round(newW)}px`;
                img.style.height = `${Math.round(newH)}px`;
                img.removeAttribute("width");
                img.removeAttribute("height");
            };

            const onUp = () => {
                window.removeEventListener("mousemove", onMove);
                window.removeEventListener("mouseup", onUp);
            };

            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
        });
    });
}

function insertWrappedImageAtRange(editor, wrap, savedRange) {
    editor.focus();

    let range = savedRange || captureEditorRange(editor);
    if (!range && editor._lastComposeRange) {
        range = editor._lastComposeRange.cloneRange();
    }

    const spacer = document.createTextNode("\u00A0");

    if (range) {
        range.deleteContents();
        const frag = document.createDocumentFragment();
        frag.appendChild(wrap);
        frag.appendChild(spacer);
        range.insertNode(frag);
        placeCaretAfter(spacer);
        editor._lastComposeRange = captureEditorRange(editor);
        return;
    }

    const quote = editor.querySelector("blockquote, hr");
    if (quote && quote.parentNode === editor) {
        editor.insertBefore(wrap, quote);
        editor.insertBefore(spacer, quote);
    } else if (quote?.parentNode) {
        quote.parentNode.insertBefore(wrap, quote);
        quote.parentNode.insertBefore(spacer, quote);
    } else if (editor.firstChild) {
        editor.insertBefore(spacer, editor.firstChild);
        editor.insertBefore(wrap, spacer);
        placeCaretAfter(spacer);
    } else {
        editor.appendChild(wrap);
        editor.appendChild(spacer);
        placeCaretAfter(spacer);
    }

    editor._lastComposeRange = captureEditorRange(editor);
}

export async function insertComposeImage(editor, dataUrl, width = 240, savedRange = null) {
    if (!editor) return null;

    const optimized = await compressImageDataUrl(dataUrl, Math.min(720, width + 120));
    const img = document.createElement("img");
    img.src = optimized;
    img.alt = "Image";
    applyImageDisplayWidth(img, width);

    const wrap = wrapComposeImage(img);
    insertWrappedImageAtRange(editor, wrap, savedRange);

    editor.querySelectorAll(".crm-resizable-img-wrap.is-selected").forEach((w) => {
        w.classList.remove("is-selected");
    });
    wrap.classList.add("is-selected");

    return wrap;
}

export function serializeEditorHtml(editor) {
    if (!editor) return "";
    const clone = editor.cloneNode(true);
    clone.querySelectorAll(".crm-img-resize-handle").forEach((el) => el.remove());
    clone.querySelectorAll(".crm-resizable-img-wrap").forEach((wrap) => {
        const img = wrap.querySelector("img");
        if (img && wrap.parentNode) {
            wrap.parentNode.insertBefore(img, wrap);
            wrap.remove();
        }
    });
    clone.querySelectorAll(".is-selected").forEach((el) => el.classList.remove("is-selected"));
    return clone.innerHTML.trim();
}

export function initComposeImageHandlers(editor, { onSelect, initKey = "compose" } = {}) {
    if (!editor) return;
    const flag = `composeImagesInit_${initKey}`;
    if (editor.dataset[flag] === "1") return;
    editor.dataset[flag] = "1";

    const remember = () => rememberEditorSelection(editor);

    editor.addEventListener("mouseup", remember);
    editor.addEventListener("keyup", remember);
    editor.addEventListener("focus", remember);
    editor.addEventListener("click", remember);

    editor.addEventListener("click", (e) => {
        const img = e.target.closest(".crm-compose-img, .crm-resizable-img-wrap img");
        if (img) {
            e.preventDefault();
            editor.querySelectorAll(".crm-resizable-img-wrap.is-selected").forEach((w) => {
                w.classList.remove("is-selected");
            });
            const wrap = wrapComposeImage(img);
            wrap.classList.add("is-selected");
            onSelect?.(img, wrap);
            return;
        }
        editor.querySelectorAll(".crm-resizable-img-wrap.is-selected").forEach((w) => {
            w.classList.remove("is-selected");
        });
        onSelect?.(null, null);
    });

    editor.addEventListener("paste", (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (const item of items) {
            if (!item.type.startsWith("image/")) continue;
            e.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;

            const savedRange = captureEditorRange(editor) || editor._lastComposeRange?.cloneRange() || null;
            const reader = new FileReader();
            reader.onload = () => {
                insertComposeImage(editor, reader.result, 280, savedRange);
            };
            reader.readAsDataURL(file);
            return;
        }
    });
}

export function enhanceExistingComposeImages(editor) {
    if (!editor) return;
    editor.querySelectorAll("img").forEach((img) => {
        if (!img.closest(".crm-resizable-img-wrap")) {
            wrapComposeImage(img);
        }
    });
}
